import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import env from './utils/env';
import './App.css';
import PageInfo from './components/PageInfo';

interface Report {
    _id: string;
    title?: string;
    author?: string;
    issuer?: string;
    reportCreationDate?: string;
    tags?: string[];
    url?: string;
    content_ref?: string;
    filename?: string;
    // Add more fields as needed
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    reports?: Report[];
    pagination?: PaginationMetadata;
}

const ReportsView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const limit = 50;

    // Initialize state from URL parameters
    const getInitialStateFromURL = () => {
        const urlParams = new URLSearchParams(location.search);
        const page = parseInt(urlParams.get('page') || '1', 10);
        const tag = urlParams.get('tag') || '';
        return { page, tag };
    };

    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(() => getInitialStateFromURL().page);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [selectedTag, setSelectedTag] = useState<string>(() => getInitialStateFromURL().tag);
    const [tagsLoading, setTagsLoading] = useState<boolean>(false);

    // Update URL parameters when state changes
    const updateURLParams = (page: number, tag: string) => {
        const urlParams = new URLSearchParams();
        if (page > 1) urlParams.set('page', page.toString());
        if (tag) urlParams.set('tag', tag);
        
        const newUrl = urlParams.toString() 
            ? `${location.pathname}?${urlParams.toString()}`
            : location.pathname;
            
        window.history.replaceState({}, '', newUrl);
    };

    const fetchTags = async () => {
        if (!authLoaded) return;
        setTagsLoading(true);
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        try {
            const response = await axios.get(`${apiUrl}/report-sources/tags`, {
                headers
            });
            if (Array.isArray(response.data)) {
                setAvailableTags(response.data);
            } else if (response.data.tags && Array.isArray(response.data.tags)) {
                setAvailableTags(response.data.tags);
            }
        } catch (error) {
            console.error('Failed to fetch tags:', error);
        } finally {
            setTagsLoading(false);
        }
    };

    const fetchReports = (page: number, tagFilter?: string) => {
        if (!authLoaded) return;
        setLoading(true);
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        let url = `${apiUrl}/reports?limit=${limit}&page=${page}`;
        if (tagFilter && tagFilter !== '') {
            url += `&tag=${encodeURIComponent(tagFilter)}`;
        }
        
        axios.get<ApiResponse>(url, {
            headers
        })
            .then(response => {
                if (Array.isArray(response.data['reports'])) {
                    setReports(response.data['reports']);
                } else {
                    setReports([]);
                }
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (authLoaded) {
            fetchTags();
            fetchReports(currentPage, selectedTag);
        }
    }, [authLoaded]);

    useEffect(() => {
        if (authLoaded) {
            fetchReports(currentPage, selectedTag);
        }
    }, [token, authLoaded]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const { page, tag } = getInitialStateFromURL();
        if (page !== currentPage || tag !== selectedTag) {
            setCurrentPage(page);
            setSelectedTag(tag);
            if (authLoaded) {
                fetchReports(page, tag);
            }
        }
    }, [location.search]);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        updateURLParams(newPage, selectedTag);
        fetchReports(newPage, selectedTag);
    };

    const handleTagChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newTag = event.target.value;
        setSelectedTag(newTag);
        setCurrentPage(1); // Reset to first page when filtering
        updateURLParams(1, newTag);
        fetchReports(1, newTag);
    };

    const clearTagFilter = () => {
        setSelectedTag('');
        setCurrentPage(1);
        updateURLParams(1, '');
        fetchReports(1, '');
    };

    if (!authLoaded || loading) {
        return (
            <div className="loading-container">
                <div className="bonds-loader-flex">
                    <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
                    <span className="loader-text">
                        {!authLoaded ? t('loading.authentication') : t('loading.reports', 'Loading reports...')}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="table-container">
            {/* Tag Filter Section */}
            <div className="reports-filter-section">
                <div className="filter-form">
                    <label htmlFor="tag-filter" className="filter-label">
                        {t('reports.filterByTag', 'Filter by Tag')}:
                    </label>
                    <select
                        id="tag-filter"
                        value={selectedTag}
                        onChange={handleTagChange}
                        className="tag-filter-select"
                        disabled={tagsLoading || loading}
                    >
                        <option value="">{t('reports.allTags', 'All Tags')}</option>
                        {availableTags.map((tag) => (
                            <option key={tag} value={tag}>
                                {tag}
                            </option>
                        ))}
                    </select>
                    {selectedTag && (
                        <button
                            type="button"
                            onClick={clearTagFilter}
                            className="clear-filter-button"
                            disabled={loading}
                        >
                            {t('reports.clearFilter', 'Clear Filter')}
                        </button>
                    )}
                    {tagsLoading && (
                        <span className="filter-loading">
                            <i className="fas fa-spinner fa-spin" />
                        </span>
                    )}
                </div>
                {selectedTag && (
                    <div className="filter-info">
                        {t('reports.filteredBy', 'Filtered by')}: <span className="filter-tag">{selectedTag}</span>
                    </div>
                )}
            </div>

            {pagination && (
                <PageInfo 
                    count={reports.length}
                    total={pagination.totalDocuments}
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    label={t('reports.label', 'reports')}
                />
            )}
            <table className="bonds-table">
                <thead>
                    <tr>
                        <th>{t('reports.title', 'Title')}</th>
                        <th>{t('reports.author', 'Author')}</th>
                        <th>{t('reports.issuer', 'Issuer')}</th>
                        <th>{t('reports.reportCreationDate', 'Report Creation Date')}</th>
                        <th>{t('reports.tags', 'Tags')}</th>
                        <th>{t('reports.actions', 'Actions')}</th>
                        {/* Add more columns as needed */}
                    </tr>
                </thead>
                <tbody>
                    {reports.length === 0 ? (
                        <tr>
                            <td colSpan={6}>{t('reports.noData', 'No reports found.')}</td>
                        </tr>
                    ) : (
                        reports.map((report) => (
                            <tr key={report._id}>
                                <td
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        // Preserve current state when navigating to detail
                                        const currentParams = new URLSearchParams();
                                        if (currentPage > 1) currentParams.set('page', currentPage.toString());
                                        if (selectedTag) currentParams.set('tag', selectedTag);
                                        const returnUrl = currentParams.toString()
                                            ? `${location.pathname}?${currentParams.toString()}`
                                            : location.pathname;
                                        navigate(`/reports/${report._id}?returnTo=${encodeURIComponent(returnUrl)}`);
                                    }}
                                >{report.title || '-'}</td>
                                <td>{report.author || '-'}</td>
                                <td>{report.issuer || '-'}</td>
                                <td className="reports-date-cell">{report.reportCreationDate || '-'}</td>
                                <td>
                                    {report.tags && report.tags.length > 0 ? (
                                        <div className="tags-container">
                                            {report.tags.map((tag, index) => (
                                                <span
                                                    key={index}
                                                    className="tag-badge"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button
                                            className="view-details-button"
                                            onClick={() => {
                                                const currentParams = new URLSearchParams();
                                                if (currentPage > 1) currentParams.set('page', currentPage.toString());
                                                if (selectedTag) currentParams.set('tag', selectedTag);
                                                const returnUrl = currentParams.toString()
                                                    ? `${location.pathname}?${currentParams.toString()}`
                                                    : location.pathname;
                                                navigate(`/reports/${report._id}?returnTo=${encodeURIComponent(returnUrl)}`);
                                            }}
                                            title="View Details"
                                        >
                                            <i className="fas fa-eye"></i> View
                                        </button>
                                        {(report.url || report.content_ref) && (
                                            <>
                                                {report.content_ref && (
                                                    <a
                                                        href={report.content_ref}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="download-button"
                                                        title="View document"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <i className="fas fa-external-link-alt"></i> Open
                                                    </a>
                                                )}
                                                {report.url && (
                                                    <a
                                                        href={report.url}
                                                        download
                                                        className="download-button"
                                                        title="Download document"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <i className="fas fa-download"></i> Download
                                                    </a>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                                {/* Add more fields as needed */}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {pagination && (
                <>
                    {console.log('Pagination metadata:', pagination)}
                    <div className="pagination-container">
                        <div className="pagination-info">
                            Showing {reports.length} of {pagination.totalDocuments} reports
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="pagination-button"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-angle-double-left"></i>
                            </button>
                            <button
                                className="pagination-button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-angle-left"></i>
                            </button>
                            <span className="pagination-page-info">
                                {t('pagination.page', 'Page')} {pagination.currentPage} {t('pagination.of', 'of')} {pagination.totalPages}
                            </span>
                            <button
                                className="pagination-button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!pagination.hasNextPage}
                            >
                                <i className="fas fa-angle-right"></i>
                            </button>
                            <button
                                className="pagination-button"
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={!pagination.hasNextPage}
                            >
                                <i className="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReportsView; 